import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FundsPortalService, LocalStorageService } from 'src/app/core/core.module';
import { AtramModalComponent } from 'src/app/shared/components/atram-modal/atram-modal.component';

@Component({
  selector: 'app-forgot-password-verify',
  templateUrl: './forgot-password-verify.component.html',
  styleUrls: ['./forgot-password-verify.component.less']
})
export class ForgotPasswordVerifyComponent implements OnInit, OnDestroy {

  @ViewChild('atramModal')
  public atramModal: AtramModalComponent = new AtramModalComponent;

  public searchFormGroup!: FormGroup;
  public isError: boolean = false;
  public isInvalidCode: boolean = false;
  public isNotValidInputs: boolean = false;
  public invalidText: string = '';
  public lockoutRemainingSeconds: number = 0;
  public isLockedOut: boolean = false;

  private lockoutInterval: any = null;

  get lockoutDisplay(): string {
    const m = Math.floor(this.lockoutRemainingSeconds / 60);
    const s = this.lockoutRemainingSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  constructor(
    public fundsPortalService: FundsPortalService,
    public router: Router,
    public localStorageService: LocalStorageService
  ) {}

  ngOnDestroy(): void {
    if (this.lockoutInterval) {
      clearInterval(this.lockoutInterval);
    }
  }

  ngOnInit(): void {
    const email = this.localStorageService.get('forgot_password_email');
    const companyCode = this.localStorageService.get('forgot_password_company_code');

    if (!email || !companyCode) {
      this.router.navigateByUrl('/forgot-password', { skipLocationChange: false });
      return;
    }

    this.initializeFormGroup();
  }

  initializeFormGroup(): void {
    this.searchFormGroup = new FormGroup({
      challenge: new FormControl('', [Validators.required])
    });
  }

  proceed(): void {
    const challenge = this.searchFormGroup.get('challenge')?.value?.trim();
    if (!challenge) {
      this.isError = true;
      this.isNotValidInputs = true;
      return;
    }

    this.isNotValidInputs = false;
    this.submit(challenge);
  }

  submit(challengeValue: string): void {
    const body = {
      email: this.localStorageService.get('forgot_password_email'),
      company_code: this.localStorageService.get('forgot_password_company_code'),
      challenge: challengeValue
    };

    this.fundsPortalService.postForgotPasswordVerify(body).subscribe({
      next: (data: any) => {
        if (data?.response_result !== '0001') {
          this.isInvalidCode = true;
          this.invalidText =
            data?.response_data?.message ||
            data?.response_description ||
            'Invalid code. Please try again.';
          return;
        }

        const responseData = data?.response_data || {};
        const forgotPasswordCode =
          responseData?.forgot_password_code ||
          responseData?.code ||
          responseData?.reset_code ||
          responseData?.token ||
          null;

        if (forgotPasswordCode) {
          this.localStorageService.set('forgot_password_code', forgotPasswordCode, 1800);
          const authToken = responseData?.auth_token;
          if (authToken?.access_token) {
            this.localStorageService.set('bearer', authToken.access_token, authToken.expires_in);
          }
          this.router.navigateByUrl(`/forgot-password/change-password?code=${encodeURIComponent(forgotPasswordCode)}`, { skipLocationChange: false });
          return;
        }

        this.atramModal.show();
        this.atramModal.size = 'sm';
        this.atramModal.status = 'error';
        this.atramModal.headerText = 'Verification Failed';
        this.atramModal.bodyText = 'Verification succeeded but reset code is missing. Please try Forgot Password again.';
        this.atramModal.okButtonText = 'Back to Forgot Password';
        this.atramModal.showCancelButton = false;
        this.atramModal.showCloseButton = true;
        this.atramModal.confirmationSubject.subscribe((confirmation: boolean) => {
          if (confirmation) {
            this.router.navigateByUrl('/forgot-password', { skipLocationChange: false });
          }
          this.atramModal.hide();
        });
      },
      error: (error: any) => {
        const responseData = error?.response_data ?? {};

        if (responseData['is_locked'] === true) {
          this.isInvalidCode = false;
          const lockoutSeconds: number = responseData?.lockout_duration_seconds ?? 300;
          const lockoutMinutes = Math.ceil(lockoutSeconds / 60);
          this.isLockedOut = true;
          this.lockoutRemainingSeconds = lockoutSeconds;
          if (this.lockoutInterval) { clearInterval(this.lockoutInterval); }
          this.lockoutInterval = setInterval(() => {
            if (this.lockoutRemainingSeconds > 0) {
              this.lockoutRemainingSeconds--;
            } else {
              clearInterval(this.lockoutInterval);
              this.isLockedOut = false;
            }
          }, 1000);
          this.atramModal.show();
          this.atramModal.size = 'sm';
          this.atramModal.status = 'error';
          this.atramModal.headerText = 'Account Temporarily Locked';
          this.atramModal.bodyText = `Your account has been temporarily locked due to too many incorrect attempts. Please wait ${lockoutMinutes} minute(s) before trying again.`;
          this.atramModal.okButtonText = 'OK';
          this.atramModal.showCancelButton = false;
          this.atramModal.showCloseButton = false;
          this.atramModal.confirmationSubject.subscribe(() => {
            this.atramModal.hide();
          });
          return;
        }

        this.isInvalidCode = true;
        this.invalidText =
          error?.response_data?.message ||
          error?.response_description ||
          'Unable to verify. Please try again.';

        if ((error?.response_data?.failed_attempt_count ?? 0) >= 4) {
          this.showLockoutWarningModal();
        }
      }
    });
  }

  private showLockoutWarningModal(): void {
    this.atramModal.show();
    this.atramModal.size = 'sm';
    this.atramModal.status = 'warning';
    this.atramModal.headerText = 'Warning: One Attempt Remaining';
    this.atramModal.bodyText = 'You have entered an incorrect code 4 times. One more incorrect attempt will lock your account.';
    this.atramModal.okButtonText = 'OK';
    this.atramModal.showCancelButton = false;
    this.atramModal.showCloseButton = false;
    this.atramModal.confirmationSubject.subscribe(() => {
      this.atramModal.hide();
    });
  }

  goToForgotPassword(): void {
    this.router.navigateByUrl('/forgot-password', { skipLocationChange: false });
  }
}
